import React from "react";
import { Table, Spinner, Badge, Alert } from "react-bootstrap";
import { useEventEntries } from "../util/db";

function AllDivisionsPayoutTab({
  eventId,
  divisions,
  scoringOrderGolf,
  throwScoringModeId,
  divisionSettings,
}) {
  const {
    data: entries,
    status: entriesStatus,
    error: entriesError,
  } = useEventEntries(eventId);

  if (entriesStatus === "loading") {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2">Loading all divisions data...</div>
      </div>
    );
  }

  if (entriesError) {
    return (
      <Alert variant="danger" className="mt-3">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>Unable to load data for all divisions. Please try again.</p>
        <small className="text-muted">Error: {entriesError?.message}</small>
      </Alert>
    );
  }

  if (!entries || entries.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No players found across all divisions</p>
      </div>
    );
  }

  // Group entries by division
  const entriesByDivision =
    entries?.reduce((acc, entry) => {
      const divisionId = entry.event_divisions_id;
      if (!acc[divisionId]) {
        acc[divisionId] = [];
      }
      acc[divisionId].push(entry);
      return acc;
    }, {}) || {};

  // Calculate totals for each division
  const divisionSummaries =
    divisions?.map((division) => {
      // Match entries using event_divisions_id
      const divisionEntries =
        entriesByDivision[division.event_divisions_id] || [];
      const totalPlayers = divisionEntries.length;
      const activePlayers = divisionEntries.filter(
        (entry) => !entry.is_dnf && !entry.is_dropped
      ).length;

      // Get settings for this specific division
      const settings = divisionSettings[division.event_divisions_id] || {
        payoutPercentage: 40,
        selectedPaytableId: null,
      };

      // Calculate number of places paid for this division
      const placesPaid = Math.ceil(
        (activePlayers * settings.payoutPercentage) / 100
      );

      // Calculate total payouts - payouts is an array from related table
      const totalPayout = divisionEntries.reduce((sum, entry) => {
        const entryPayouts = entry.payouts || [];
        const entryPayoutSum = entryPayouts.reduce(
          (payoutSum, payout) => payoutSum + (payout.payout_amount || 0),
          0
        );
        return sum + entryPayoutSum;
      }, 0);

      const totalFees = (division.division_cost || 0) * totalPlayers;
      const totalRake = (division.division_rake_per_player || 0) * totalPlayers;
      const availablePayout =
        ((division.division_cost || 0) -
          (division.division_rake_per_player || 0)) *
        totalPlayers;

      return {
        ...division,
        totalPlayers,
        activePlayers,
        placesPaid,
        totalPayout,
        totalFees,
        totalRake,
        availablePayout,
        entries: divisionEntries,
        settings, // Include the division-specific settings
      };
    }) || [];

  // Calculate grand totals
  const grandTotals = divisionSummaries.reduce(
    (totals, division) => ({
      totalPlayers: totals.totalPlayers + division.totalPlayers,
      activePlayers: totals.activePlayers + division.activePlayers,
      totalPlacesPaid: totals.totalPlacesPaid + division.placesPaid,
      totalFees: totals.totalFees + division.totalFees,
      totalRake: totals.totalRake + division.totalRake,
      totalAvailablePayout:
        totals.totalAvailablePayout + division.availablePayout,
      totalPayout: totals.totalPayout + division.totalPayout,
    }),
    {
      totalPlayers: 0,
      activePlayers: 0,
      totalPlacesPaid: 0,
      totalFees: 0,
      totalRake: 0,
      totalAvailablePayout: 0,
      totalPayout: 0,
    }
  );

  return (
    <div className="mt-3">
      {/* Summary Cards */}
      <div className="row g-2 mb-4">
        <div className="col-6 col-sm-3">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-primary"
              style={{ whiteSpace: "nowrap" }}
            >
              {grandTotals.totalPlayers}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Total Players
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-3">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-success"
              style={{ whiteSpace: "nowrap" }}
            >
              ${grandTotals.totalAvailablePayout.toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Total Prize Pool
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-3">
          <div className="text-center p-2 bg-light rounded h-100">
            <div
              className="h6 mb-1 text-danger"
              style={{ whiteSpace: "nowrap" }}
            >
              ${grandTotals.totalRake.toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Fees Collected
            </small>
          </div>
        </div>
        <div className="col-6 col-sm-3">
          <div className="text-center p-2 bg-light rounded h-100">
            <div className="h6 mb-1 text-info" style={{ whiteSpace: "nowrap" }}>
              ${grandTotals.totalFees.toFixed(2)}
            </div>
            <small
              className="text-muted d-block"
              style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
            >
              Total Revenue
            </small>
          </div>
        </div>
      </div>

      {/* Division Breakdown Table */}
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead style={{ background: "#2D083F", color: "white" }}>
            <tr>
              <th>Division</th>
              <th className="text-center">Players</th>
              <th className="text-center">Places Paid</th>
              <th className="text-center">Entry Cost</th>
              <th className="text-center">Fees</th>
              <th className="text-center">Prize Pool</th>
              <th className="text-center">Paid Out</th>
              <th className="text-center">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {divisionSummaries.map((division) => {
              const remaining = division.availablePayout - division.totalPayout;

              return (
                <tr key={division.event_divisions_id}>
                  <td>{division.divisions.name}</td>
                  <td className="text-center">
                    <Badge bg="info">{division.totalPlayers}</Badge>
                  </td>
                  <td className="text-center">
                    <Badge bg="warning" text="dark">
                      {division.placesPaid} (
                      {division.settings.payoutPercentage}%)
                    </Badge>
                  </td>
                  <td className="text-center text-info">
                    ${(division.division_cost || 0).toFixed(2)}
                  </td>
                  <td className="text-center text-danger">
                    ${(division.division_rake_per_player || 0).toFixed(2)}
                  </td>
                  <td className="text-center text-success">
                    <strong>${division.availablePayout.toFixed(2)}</strong>
                  </td>
                  <td className="text-center">
                    ${division.totalPayout.toFixed(2)}
                  </td>
                  <td className="text-center">
                    <span
                      className={
                        remaining > 0
                          ? "text-success"
                          : remaining < 0
                          ? "text-danger"
                          : "text-muted"
                      }
                    >
                      ${remaining.toFixed(2)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot style={{ background: "#f8f9fa", fontWeight: "bold" }}>
            <tr>
              <td>
                <strong>Totals</strong>
              </td>
              <td className="text-center">
                <Badge bg="info">{grandTotals.totalPlayers}</Badge>
              </td>
              <td className="text-center">
                <Badge bg="warning" text="dark">
                  {grandTotals.totalPlacesPaid}
                </Badge>
              </td>
              <td className="text-center">—</td>
              <td className="text-center text-danger">
                <strong>${grandTotals.totalRake.toFixed(2)}</strong>
              </td>
              <td className="text-center text-success">
                <strong>${grandTotals.totalAvailablePayout.toFixed(2)}</strong>
              </td>
              <td className="text-center">
                <strong>${grandTotals.totalPayout.toFixed(2)}</strong>
              </td>
              <td className="text-center">
                <span
                  className={
                    grandTotals.totalAvailablePayout - grandTotals.totalPayout >
                    0
                      ? "text-success"
                      : grandTotals.totalAvailablePayout -
                          grandTotals.totalPayout <
                        0
                      ? "text-danger"
                      : "text-muted"
                  }
                >
                  <strong>
                    $
                    {(
                      grandTotals.totalAvailablePayout - grandTotals.totalPayout
                    ).toFixed(2)}
                  </strong>
                </span>
              </td>
            </tr>
          </tfoot>
        </Table>
      </div>
    </div>
  );
}

export default AllDivisionsPayoutTab;
